import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import { Avatar, Box, Button, IconButton, Flex, Divider, SplitButton, SplitButtonMenu, MenuItem, Text, Icon, Tooltip } from '@vibe/core';
import {
  Board as BoardIcon,
  Locked,
  Edit as EditIcon,
  Show,
  Duplicate as DuplicateIcon,
  Status as StatusIcon,
  TurnInto as TurnIntoIcon,
} from '@vibe/icons';

import { useNavigate } from 'react-router';
import { Template } from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import CreatedBy from '../shared/components/CreatedBy';
import { InstantUpdateModal } from './ToMondayModal';

function getQuickActions(template: Template) {
  const templateConfig = template.config;
  const results: any[] = [];

  if (!templateConfig) {
    return results;
  }

  for (const sheet of templateConfig.sheets) {
    for (const fieldGroup of sheet.field_groups) {
      for (let i = 0; i < fieldGroup.columns.length; i++) {
        const column = fieldGroup.columns[i];
        const pathPiece = column.path[column.path.length - 1];

        // @ts-ignore
        const isCustomFormula = pathPiece?.field_id === 'custom-formula';

        if (
          isCustomFormula &&
          column.config?.to_monday_target_column_id &&
          column.config?.to_monday_is_quick_actions &&
          column.config?.to_monday_quick_action_name
        ) {
          let initialCtx: any = {};

          if (column.config?.to_monday_target_column_id) {
            initialCtx.targetColumnId = column.config.to_monday_target_column_id;
          }

          if (_.isBoolean(column.config?.to_monday_is_quick_actions)) {
            initialCtx.isQuickAction = column.config.to_monday_is_quick_actions;
          }

          if (column.config?.to_monday_quick_action_name) {
            initialCtx.quickActionName = column.config.to_monday_quick_action_name;
          }

          results.push({
            template: template,
            column,
            fieldGroup,
            initialCtx,
            formulaColumnIdx: i,
            sheet,
            label: column.config?.to_monday_quick_action_name,
          });
        }
      }
    }
  }

  return results;
}

type TemplatesListProps = {
  templateSearchTerm: string;
  mondayContext: { boardId: number };
  templates: any[];
  onDeleteClick: (template: any) => void;
  onDuplicateClick: (template: any) => void;
  onExportClick: (template: any) => void;
};

export function TemplatesList({ templateSearchTerm, templates, onDuplicateClick, onExportClick, mondayContext }: TemplatesListProps) {
  const [quickActionModal, setQuickActionModal] = useState<any>();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {quickActionModal ? (
        <InstantUpdateModal
          onClose={() => {
            setQuickActionModal(null);
          }}
          show={true}
          initialCtx={quickActionModal.initialCtx}
          formulaColumnIdx={quickActionModal.formulaColumnIdx}
          template={quickActionModal.template}
          fieldGroup={quickActionModal.fieldGroup}
          sheet={quickActionModal.sheet}
        />
      ) : null}
      <Box>
        {_.orderBy(templates, 'name').map((template, i) => {
          const quickActions = getQuickActions(template);

          return (
            <div
              key={template.id}
              style={{
                display: (() => {
                  const term = (templateSearchTerm || '').trim();
                  const shouldFilter = term.length >= 3;
                  if (!shouldFilter) return 'block';
                  const matches = (template.name || '').toLowerCase().includes(term.toLowerCase());
                  return matches ? 'block' : 'none';
                })(),
              }}
            >
              <Box>
                <Box padding="medium" paddingStart="large" paddingEnd="large" className="list--element">
                  <Flex gap="medium" align="center" direction="row">
                    <Avatar
                      type="icon"
                      size="medium"
                      withoutBorder={true}
                      icon={StatusIcon}
                      customBackgroundColor="#0073ea"
                      square
                      onClick={() => {
                        navigate(`/template/${template.id}/edit`);
                      }}
                    />

                    <Flex
                      gap="large"
                      align="center"
                      direction="row"
                      onClick={() => {
                        navigate(`/template/${template.id}/edit`);
                      }}
                    >
                      <Box paddingEnd="xs">
                        <Flex gap="xs" style={{ maxWidth: '300px' }}>
                          <Text type="text2" weight="medium" ellipsis>
                            {template.name}
                          </Text>
                          <>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'nowrap',
                                gap: '6px',
                                marginLeft: '8px',
                                height: '28px',
                              }}
                            >
                              {(template.boards || []).some((boardId: number) => boardId === mondayContext.boardId) ? (
                                <div style={{ transform: 'translate3d(0,4px,0)' }}>
                                  <Tooltip content="Current board is used in this template">
                                    <Icon icon={BoardIcon} size={17} />
                                  </Tooltip>
                                </div>
                              ) : null}

                              {template.private ? (
                                <div style={{ transform: 'translate3d(0,3px,0)' }}>
                                  <Tooltip content="Private template is hidden to others">
                                    <Icon icon={Locked} size={17} />
                                  </Tooltip>
                                </div>
                              ) : null}
                            </div>
                          </>
                        </Flex>
                        <div style={{ marginTop: '-3px' }}>
                          <CreatedBy userId={template.creatorId} time={template.createdAt} userAvatar={false} />
                        </div>
                      </Box>
                    </Flex>

                    <Box style={{ marginLeft: 'auto' }}>
                      <Flex gap={0} align="center" direction="row">
                        {quickActions.length > 0 ? (
                          quickActions.length < 4 && !isMobile ? (
                            <Flex gap={8} style={{ marginLeft: '20px', marginRight: '20px' }}>
                              {quickActions.map((quickAction, idx) => {
                                return (
                                  <Button
                                    key={idx}
                                    leftIcon={TurnIntoIcon}
                                    size="small"
                                    kind="tertiary"
                                    onClick={() => {
                                      setQuickActionModal(quickAction);
                                    }}
                                  >
                                    {quickAction.label}
                                  </Button>
                                );
                              })}
                            </Flex>
                          ) : (
                            <div style={{ marginLeft: '20px', marginRight: '20px' }}>
                              <SplitButton
                                leftIcon={TurnIntoIcon}
                                size="small"
                                kind="secondary"
                                secondaryDialogContent={
                                  <SplitButtonMenu id="split-menu-actions">
                                    {quickActions.map((quickAction, index) => {
                                      return index !== 0 ? (
                                        <MenuItem
                                          icon={TurnIntoIcon}
                                          title={quickAction.label}
                                          onClick={() => {
                                            setQuickActionModal(quickAction);
                                          }}
                                        />
                                      ) : null;
                                    })}
                                  </SplitButtonMenu>
                                }
                                onClick={() => {
                                  setQuickActionModal(quickActions[0]);
                                }}
                              >
                                {quickActions[0].label}
                              </SplitButton>
                            </div>
                          )
                        ) : null}

                        <IconButton
                          icon={Show}
                          size="small"
                          tooltipContent="Preview template"
                          onClick={() => {
                            navigate(`/template/${template.id}/render`);
                          }}
                        />
                        <IconButton
                          icon={DuplicateIcon}
                          size="small"
                          tooltipContent="Duplicate template"
                          onClick={() => {
                            onDuplicateClick(template);
                          }}
                        />
                        <IconButton
                          icon={EditIcon}
                          size="small"
                          tooltipContent="Edit template"
                          onClick={() => {
                            navigate(`/template/${template.id}/edit`);
                          }}
                        />
                        <Flex gap="small" align="center" direction="row" style={{ marginLeft: '10px' }}>
                          <Button
                            size="small"
                            kind="primary"
                            onClick={() => {
                              onExportClick(template);
                            }}
                          >
                            Export
                          </Button>
                        </Flex>
                      </Flex>
                    </Box>
                  </Flex>
                </Box>
              </Box>
              {templates.length === i + 1 ? null : <Divider withoutMargin={true} />}
            </div>
          );
        })}
      </Box>
    </>
  );
}
